import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { MatDialogRef } from '@angular/material/dialog';
import { ApiService } from 'src/app/shared/services/api.service';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';

export interface MinimizedMeetingData {
  meetingId: string;
  meetingTitle: string;
  status: string;
  dialogType: 'admin' | 'join' | 'waiting';
  dialogData: any;
  dialogRef?: MatDialogRef<any>;
}

@Injectable({
  providedIn: 'root'
})
export class MinimizedMeetingService {
  private minimizedMeetingsSubject = new BehaviorSubject<MinimizedMeetingData[]>([]);
  public minimizedMeetings$: Observable<MinimizedMeetingData[]> = this.minimizedMeetingsSubject.asObservable();

  constructor(
    private apiService: ApiService
  ) {
    this.loadFromStorage();
  }

  // Add a minimized meeting
  addMinimizedMeeting(meeting: MinimizedMeetingData): void {
    const current = this.minimizedMeetingsSubject.value;
    // Remove if already exists
    const filtered = current.filter(m => !this.meetingMatches(m, meeting.meetingId));
    // Add new one
    const updated = [...filtered, meeting];
    this.minimizedMeetingsSubject.next(updated);
    this.saveToStorage(updated);
  }

  // Remove a minimized meeting
  removeMinimizedMeeting(meetingId: string | number): void {
    const current = this.minimizedMeetingsSubject.value;
    const updated = current.filter(m => !this.meetingMatches(m, meetingId));
    this.minimizedMeetingsSubject.next(updated);
    this.saveToStorage(updated);
  }

  // Update meeting status in minimized list
  updateMeetingStatus(meetingId: string | number, newStatus: string): void {
    const current = this.minimizedMeetingsSubject.value;
    const updated = current.map(m => {
      if (this.meetingMatches(m, meetingId)) {
        return { ...m, status: newStatus };
      }
      return m;
    });
    this.minimizedMeetingsSubject.next(updated);
    this.saveToStorage(updated);
  }

  // Remove meetings by status (e.g., Completed, Cancelled)
  removeMeetingsByStatus(statuses: string[]): void {
    const current = this.minimizedMeetingsSubject.value;
    const updated = current.filter(m => {
      const meetingStatus = m.status?.toString().toLowerCase() || '';
      return !statuses.some(s => meetingStatus === s.toLowerCase());
    });
    this.minimizedMeetingsSubject.next(updated);
    this.saveToStorage(updated);
  }

  // Get minimized meetings
  getMinimizedMeetings(): MinimizedMeetingData[] {
    return this.minimizedMeetingsSubject.value;
  }

  // Clear all minimized meetings (e.g., on logout)
  clearAllMinimizedMeetings(): void {
    this.minimizedMeetingsSubject.next([]);
    try {
      localStorage.removeItem('minimizedMeetings');
    } catch (e) {
      console.error('Error clearing minimized meetings from storage:', e);
    }
  }

  private normalizeMeetingId(value: any): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private meetingMatches(meeting: MinimizedMeetingData, meetingId: any): boolean {
    const target = this.normalizeMeetingId(meetingId);
    if (!target) {
      return false;
    }

    const candidates = [
      meeting.meetingId,
      meeting.dialogData?.meetingId,
      meeting.dialogData?.meetingIdInt,
      meeting.dialogData?.id
    ];

    return candidates.some(candidate => this.normalizeMeetingId(candidate) === target);
  }

  // Check for active meetings (OnGoing or WaitingForAttendance) after login
  async checkActiveMeetingsAfterLogin(userEmail: string): Promise<void> {
    try {
      console.log('checkActiveMeetingsAfterLogin called with email:', userEmail);
      
      if (!userEmail) {
        console.warn('No user email provided, cannot check active meetings');
        return;
      }
      
      // Single API call to get all meetings - will be used for both validation and checking new meetings
      const url = `${environment.API_URL}/api/Meetings`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      
      // First, validate and cleanup existing minimized meetings using the fetched data
      await this.validateAndCleanupMeetingsWithData(response?.data || []);
      
      console.log('Meetings API response:', response);
      
      if (response?.isSuccess && response?.data && Array.isArray(response.data)) {
        console.log(`Total meetings found: ${response.data.length}`);
        
        // First, filter for active meetings (OnGoing or WaitingForAttendance)
        const activeMeetingsList = response.data.filter((meeting: any) => {
          const isActiveStatus = meeting.status === 'OnGoing' || 
                                meeting.status === 'WaitingForAttendance' ||
                                meeting.status === 3 || 
                                meeting.status === 2;
          return isActiveStatus;
        });
        
        console.log(`Found ${activeMeetingsList.length} active meetings (OnGoing or WaitingForAttendance)`);
        
        // Now fetch each active meeting individually to get members and verify user is invited
        const activeMeetings: any[] = [];
        
        for (const meeting of activeMeetingsList) {
          try {
            const meetingId = meeting.meetingId || meeting.id?.toString();
            if (!meetingId) {
              console.warn('Meeting missing ID, skipping:', meeting);
              continue;
            }
            
            // Fetch full meeting details to get members
            const meetingUrl = `${environment.API_URL}/api/Meetings/${meetingId}`;
            const meetingResponse: any = await lastValueFrom(this.apiService.getByUrl(meetingUrl));
            
            if (meetingResponse?.isSuccess && meetingResponse?.data) {
              const fullMeeting = meetingResponse.data;
              
              // Check if user is invited to the meeting
              // Try multiple ways to match email
              const isInvited = fullMeeting.members && Array.isArray(fullMeeting.members) &&
                               fullMeeting.members.some((member: any) => {
                                 const memberEmail = member.userEmail || 
                                                   member.email || 
                                                   member.emailAddress || 
                                                   member.EmailAddress ||
                                                   member.Email ||
                                                   '';
                                 return memberEmail && memberEmail.toLowerCase() === userEmail.toLowerCase();
                               });
              
              if (isInvited) {
                console.log('Found active meeting user is invited to:', fullMeeting.meetingTitle || meeting.meetingTitle, fullMeeting.statusName || meeting.status);
                activeMeetings.push(fullMeeting);
              } else {
                console.log('User not invited to meeting:', fullMeeting.meetingTitle || meeting.meetingTitle);
              }
            }
          } catch (error) {
            console.error(`Error fetching meeting ${meeting.meetingId}:`, error);
            // Continue with next meeting
          }
        }
        
        console.log(`Found ${activeMeetings.length} active meetings user is invited to`);
        
        // Add active meetings to minimized list
        // Use a longer delay to ensure any dialogs that are opening have time to render
        // This prevents adding meetings that are being auto-restored on page refresh
        setTimeout(() => {
          activeMeetings.forEach((meeting: any) => {
            // Determine dialog type based on user role and meeting status
            // For now, we'll use 'join' as default, but this could be enhanced
            const dialogType: 'admin' | 'join' | 'waiting' = 
              meeting.status === 'WaitingForAttendance' || meeting.status === 2 || meeting.statusName === 'WaitingForAttendance' ? 'waiting' :
              'join'; // Could be 'admin' if user is hoster, but we'll determine that when restoring
            
            // Map agenda items from full meeting data
            const agendaItems = meeting.agendaItems?.map((ai: any, index: number) => ({
              subject: ai.subject,
              agendaTitleAR: ai.agendaTitleAR,
              agendaTitleEN: ai.agendaTitleEN,
              participants: meeting.members?.map((m: any) => m.userName).join(', ') || '',
              dateTime: new Date(meeting.dateTime).toISOString(),
              duration: '30 mins',
              presenter: meeting.members?.[0]?.userName || '',
              status: 'Pending',
              descriptionEnglish: ai.agendaDescriptionEN || ai.agendaDescriptionEn || ai.tcNotes || ai.subject,
              descriptionArabic: ai.agendaDescriptionAR || ai.agendaDescriptionAr || ai.agendaTitleAR || ai.subject,
              prIdentifiers: ai.prIdentifiers || [],
              currentStage: ai.currentStage,
              requiredFromTC: ai.requiredFromTC,
              department: ai.department,
              agendaDescriptionEN: ai.agendaDescriptionEN || ai.agendaDescriptionEn || '',
              agendaDescriptionAR: ai.agendaDescriptionAR || ai.agendaDescriptionAr || ''
            })) || [];
            
            const minimizedMeeting: MinimizedMeetingData = {
              meetingId: meeting.meetingId || meeting.id?.toString() || '',
              meetingTitle: meeting.meetingTitle || meeting.title || 'Meeting',
              status: meeting.statusName || meeting.status?.toString() || '',
              dialogType: dialogType,
              dialogData: {
                meetingTitle: meeting.meetingTitle || meeting.title || 'Meeting',
                meetingId: meeting.meetingId || meeting.id?.toString() || '',
                meetingIdInt: meeting.id,
                id: meeting.id,
                agendaItems: agendaItems,
                currentAgendaIndex: 0,
                meetingStatus: meeting.statusName || meeting.status
              }
            };
            
            // Only add if not already in minimized list
            const existing = this.minimizedMeetingsSubject.value.find(
              m => m.meetingId === minimizedMeeting.meetingId
            );
            
            if (!existing) {
              // CRITICAL: Check if a dialog is already open for this meeting
              // If dialog is open, don't add to minimized list (toaster should not show)
              let isDialogOpen = false;
              try {
                const overlayContainer = document.querySelector('.cdk-overlay-container');
                const dialogElement = overlayContainer?.querySelector('.mat-dialog-container');
                isDialogOpen = !!dialogElement;
              } catch (e) {
                isDialogOpen = false;
              }
              
              // CRITICAL: Also check if we're on the meeting details page for this specific meeting
              // If we are, the dialog is being auto-restored, so don't add to minimized list
              let isOnMeetingPage = false;
              try {
                const currentUrl = window.location.pathname;
                const meetingUrl = `/meetings/view/${minimizedMeeting.meetingId}`;
                isOnMeetingPage = currentUrl === meetingUrl || currentUrl.includes(`/meetings/view/${minimizedMeeting.meetingId}`);
              } catch (e) {
                isOnMeetingPage = false;
              }
              
              // Don't add if dialog is open OR if we're on the meeting details page for THIS specific meeting
              // (dialog is being auto-restored on page refresh)
              if (isDialogOpen || isOnMeetingPage) {
                console.log('Skipping adding meeting to minimized list - dialog is open or being auto-restored on meeting page:', minimizedMeeting.meetingTitle);
                return;
              }
              
              console.log('Adding meeting to minimized list:', minimizedMeeting.meetingTitle);
              this.addMinimizedMeeting(minimizedMeeting);
            } else {
              console.log('Meeting already in minimized list:', minimizedMeeting.meetingTitle);
            }
          });
        }, 1000); // Longer delay to ensure dialogs have time to render and auto-restore completes
        
        console.log(`Successfully processed ${activeMeetings.length} active meetings after login`);
      } else {
        console.warn('Invalid response from meetings API:', response);
      }
    } catch (error) {
      console.error('Error checking active meetings after login:', error);
      // Don't throw - this is a background operation
    }
  }

  // Save to localStorage
  private saveToStorage(meetings: MinimizedMeetingData[]): void {
    try {
      // Don't save dialogRef as it's not serializable
      const data = meetings.map(m => ({
        meetingId: m.meetingId,
        meetingTitle: m.meetingTitle,
        status: m.status,
        dialogType: m.dialogType,
        dialogData: m.dialogData
      }));
      localStorage.setItem('minimizedMeetings', JSON.stringify(data));
    } catch (e) {
      console.error('Error saving minimized meetings:', e);
    }
  }

  // Load from localStorage
  private loadFromStorage(): void {
    try {
      const data = localStorage.getItem('minimizedMeetings');
      if (data) {
        const meetings = JSON.parse(data);
        // Filter out completed/cancelled meetings immediately on load
        const activeMeetings = meetings.filter((meeting: MinimizedMeetingData) => {
          const status = meeting.status?.toString().toLowerCase() || '';
          const isCompleted = status === 'completed' || status === '5' || status === 'completed';
          const isCancelled = status === 'cancelled' || status === '4' || status === 'cancelled';
          return !isCompleted && !isCancelled;
        });
        
        // If any meetings were filtered out, update storage
        if (activeMeetings.length !== meetings.length) {
          console.log(`Filtered out ${meetings.length - activeMeetings.length} completed/cancelled meetings from storage`);
          this.saveToStorage(activeMeetings);
        }
        
        this.minimizedMeetingsSubject.next(activeMeetings);
      }
    } catch (e) {
      console.error('Error loading minimized meetings:', e);
    }
  }

  // Validate and clean up stale meetings using provided data (to avoid duplicate API calls)
  private validateAndCleanupMeetingsWithData(allMeetings: any[]): void {
    try {
      const currentMeetings = this.minimizedMeetingsSubject.value;
      if (currentMeetings.length === 0) {
        return;
      }

      console.log(`Validating ${currentMeetings.length} minimized meetings...`);
      const validMeetings: MinimizedMeetingData[] = [];

      for (const minimizedMeeting of currentMeetings) {
        // Find the meeting in the provided data
        const currentMeeting = allMeetings.find((m: any) => 
          (m.meetingId === minimizedMeeting.meetingId) || 
          (m.id?.toString() === minimizedMeeting.meetingId)
        );

        if (currentMeeting) {
          // Check if meeting is still active
          const status = currentMeeting.status || currentMeeting.statusName || '';
          const isActive = status === 'OnGoing' || 
                         status === 'WaitingForAttendance' || 
                         status === 3 || 
                         status === 2 ||
                         status === '3' ||
                         status === '2';
          
          if (isActive) {
            // Update status if it changed
            if (minimizedMeeting.status !== status.toString()) {
              minimizedMeeting.status = status.toString() || currentMeeting.statusName || '';
              console.log(`Updated meeting status: ${minimizedMeeting.meetingTitle} -> ${minimizedMeeting.status}`);
            }
            validMeetings.push(minimizedMeeting);
          } else {
            console.log(`Removing stale meeting: ${minimizedMeeting.meetingTitle} (status: ${status})`);
          }
        } else {
          // Meeting not found in API, might be deleted - remove it
          console.log(`Removing meeting not found in API: ${minimizedMeeting.meetingTitle}`);
        }
      }

      // Update the list if any meetings were removed
      if (validMeetings.length !== currentMeetings.length) {
        console.log(`Cleaned up ${currentMeetings.length - validMeetings.length} stale meetings`);
        this.minimizedMeetingsSubject.next(validMeetings);
        this.saveToStorage(validMeetings);
      }
    } catch (error) {
      console.error('Error validating minimized meetings:', error);
      // Don't throw - this is a background cleanup operation
    }
  }

  // Validate and clean up stale meetings (call this periodically or on app init)
  async validateAndCleanupMeetings(): Promise<void> {
    try {
      const currentMeetings = this.minimizedMeetingsSubject.value;
      if (currentMeetings.length === 0) {
        return;
      }

      console.log(`Validating ${currentMeetings.length} minimized meetings...`);

      // Fetch all meetings to check their current status
      const url = `${environment.API_URL}/api/Meetings`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));

      if (response?.isSuccess && response?.data && Array.isArray(response.data)) {
        this.validateAndCleanupMeetingsWithData(response.data);
      }
    } catch (error) {
      console.error('Error validating minimized meetings:', error);
      // Don't throw - this is a background cleanup operation
    }
  }
}

